import {
  Add,
  AccountBalanceWallet,
  Checklist,
  DeleteTwoTone,
  Download,
  FileUpload,
  Handshake,
  Payment,
  PlaylistRemove
} from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip
} from '@mui/material';
import {
  GridToolbarContainer,
  GridRowSelectionModel,
  GridToolbar as DefaultGridToolbar,
  GridRowsProp
} from '@mui/x-data-grid';
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import {
  Company,
  CompanyIban,
  DocumentType,
  InvoiceStages,
  PaymentStatus
} from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { useModal } from 'hooks/useModal';

import {
  gridToolbarContainerStyle,
  gridToolbarStyle,
  defaultGridToolbarStyle,
  gridToolbarOutsideContainerStyle,
  toolbarIconsStyle,
  toolbarButtonStyle
} from 'styles/components/DataGridStyle';

import {
  companyAtom,
  exportButtonTooltipTextAtom,
  isExportButtonDisabledAtom,
  selectedCompanyIdAtom
} from 'state/state';

import { Modal } from '../Modal/Modal';

interface GridToolbarProps {
  readonly handleAddClick?: () => void;
  readonly handleDeleteClick?: () => void;
  readonly isExportDisabled?: boolean;
  readonly handleExportExcelClick?: () => void;
  readonly handleUploadInvoice?: (
    fileData: FileList | File[],
    field: string
  ) => Promise<void>;
  readonly handleAccountClick?: () => void;
  readonly handleSettleClick?: () => void;
  readonly handlePayClick?: () => void;
  readonly handleApproveClick?: () => void;
  readonly handleRejectClick?: () => void;
  readonly handleDownloadClick?: () => void;
  readonly handleCreateClick?: () => void;
  readonly entries: GridRowsProp;
  readonly selectedRows?: GridRowSelectionModel;
  readonly isDefaultToolbarHidden?: boolean;
  readonly isDeleteDisabled?: boolean;
  readonly deleteButtonTooltip?: string;
  readonly companyIbans?: CompanyIban[];
  readonly companies?: Company[];
  readonly isOnAllCompanies?: boolean;
}

export const GridToolbar = ({
  handleAddClick,
  handleDeleteClick,
  handleExportExcelClick,
  handleUploadInvoice,
  handleAccountClick,
  handleSettleClick,
  handlePayClick,
  handleApproveClick,
  handleRejectClick,
  handleDownloadClick,
  handleCreateClick,
  entries,
  selectedRows,
  isDefaultToolbarHidden,
  isExportDisabled,
  isDeleteDisabled,
  deleteButtonTooltip,
  companyIbans,
  companies,
  isOnAllCompanies
}: GridToolbarProps) => {
  const { translate } = useTranslations();
  const [company] = useAtom(companyAtom);
  const [selectedCompanyId, setSelectedCompanyId] = useAtom(
    selectedCompanyIdAtom
  );
  const [isExportButtonDisabled, setIsExportButtonDisabled] = useAtom(
    isExportButtonDisabledAtom
  );
  const [exportTooltip, setExportTooltip] = useAtom(
    exportButtonTooltipTextAtom
  );

  const {
    isOpen: isSelectCompanyIdModalOpen,
    openModal: openSelectCompanyIdModal,
    closeModal: closeSelectCompanyIdModal
  } = useModal();

  const justifyContent =
    selectedRows && selectedRows.length > 0 ? 'space-between' : 'flex-end';
  const noRowsAreSelected = selectedRows && selectedRows.length === 0;
  const filteredEntries = (entries || []).filter((entry: any) =>
    selectedRows?.includes(entry.id)
  );
  const selectedExportEntries = filteredEntries.filter(
    (entry: any) => entry.documentType !== DocumentType.CREDIT_NOTE
  );
  const hasNoExportableInvoices =
    filteredEntries.length > 0 && selectedExportEntries.length === 0;
  const shouldDisableDelete = filteredEntries.some(
    (entry) => !entry.isDeletable
  );
  const shouldDisablePay = !filteredEntries.every(
    (entry) =>
      entry.stage === InvoiceStages.APPROVED &&
      (entry.paymentStatus === PaymentStatus.NOT_PAID ||
        entry.paymentStatus === PaymentStatus.OPEN_BANKING_FAILED)
  );
  const areNotInStageApproved = !filteredEntries.every(
    (entry) => entry.stage === InvoiceStages.APPROVED
  );
  const shouldShowAlreadyPaidTooltip = filteredEntries.some(
    (entry) =>
      entry.paymentStatus !== PaymentStatus.NOT_PAID &&
      entry.paymentStatus !== PaymentStatus.OPEN_BANKING_FAILED
  );
  const isNotApprovedOrPaid = filteredEntries.some(
    (entry) =>
      entry.stage !== InvoiceStages.APPROVED &&
      entry.stage !== InvoiceStages.FINALIZED
  );
  const isAlreadyBooked = filteredEntries.some((entry) => entry.isAccounted);
  const shouldDisableBook = isNotApprovedOrPaid || isAlreadyBooked;
  const shouldDisableSettle = filteredEntries.some(
    (entry) => entry.stage !== InvoiceStages.VALIDATED
  );
  const isNoDocumentType = filteredEntries.some(
    (entry) => entry.documentType === DocumentType.NO_DOCUMENT
  );
  const isNotExportable = !filteredEntries.every((entry) => entry.isExportable);
  const isIbanNotExportable = useMemo(() => {
    return (companyIbans || []).every((iban) => !iban.isExportable);
  }, [companyIbans]);
  const isCounterpartyNotValidated = !filteredEntries.every(
    (entry) => entry.isCounterpartyValidated
  );
  const shouldDisablePayForExport = !selectedExportEntries.every(
    (entry: any) =>
      entry.stage === InvoiceStages.APPROVED &&
      (entry.paymentStatus === PaymentStatus.NOT_PAID ||
        entry.paymentStatus === PaymentStatus.OPEN_BANKING_FAILED)
  );
  const shouldShowAlreadyPaidForExport = selectedExportEntries.some(
    (entry: any) =>
      entry.paymentStatus !== PaymentStatus.NOT_PAID &&
      entry.paymentStatus !== PaymentStatus.OPEN_BANKING_FAILED
  );
  const isNoDocumentTypeForExport = selectedExportEntries.some(
    (entry: any) => entry.documentType === DocumentType.NO_DOCUMENT
  );
  const isNotExportableForExport = !selectedExportEntries.every(
    (entry: any) => entry.isExportable
  );
  const isCounterpartyNotValidatedForExport = !selectedExportEntries.every(
    (entry: any) => entry.isCounterpartyValidated
  );
  const shouldDisableExport = useMemo(() => {
    return (
      hasNoExportableInvoices ||
      shouldDisablePayForExport ||
      isNoDocumentTypeForExport ||
      isIbanNotExportable ||
      isCounterpartyNotValidatedForExport ||
      isNotExportableForExport
    );
  }, [
    hasNoExportableInvoices,
    shouldDisablePayForExport,
    isNoDocumentTypeForExport,
    isIbanNotExportable,
    isCounterpartyNotValidatedForExport,
    isNotExportableForExport
  ]);
  const shouldDisableDownload = filteredEntries.some(
    (entry) =>
      entry.stage === InvoiceStages.UPLOADED ||
      entry.documentType === DocumentType.NO_DOCUMENT
  );

  const deleteButtonTooltipText = useMemo(() => {
    if (noRowsAreSelected) {
      return translate('warnings.noSelectedRows');
    }
    if (shouldDisableDelete || isDeleteDisabled) {
      return deleteButtonTooltip;
    }

    return '';
  }, [selectedRows, entries, translate]);

  const settleButtonTooltipText = useMemo(() => {
    if (noRowsAreSelected) {
      return translate('warnings.noSelectedRows');
    }
    if (shouldDisableSettle) {
      return translate('messages.disabledSettleRevenue');
    }

    return '';
  }, [selectedRows, entries, translate]);

  const payButtonTooltipText = useMemo(() => {
    if (noRowsAreSelected) {
      return translate('warnings.noSelectedRows');
    }
    if (shouldDisablePay && !areNotInStageApproved) {
      return translate('messages.disabledPayInvoices');
    }
    if (shouldShowAlreadyPaidTooltip) {
      return translate('messages.disabledPayInvoicesAlreadyPaid');
    }

    return '';
  }, [selectedRows, entries, translate]);

  const bookButtonTooltipText = useMemo(() => {
    if (noRowsAreSelected) {
      return translate('warnings.noSelectedRows');
    }
    if (isNotApprovedOrPaid) {
      return translate('messages.disabledBookedInvoicesNotApprovedOrPaird');
    }
    if (isAlreadyBooked) {
      return translate('messages.disabledBookedInvoicesAlreadyBooked');
    }

    return '';
  }, [selectedRows, entries, translate]);

  const approveButtonTooltipText = useMemo(() => {
    if (noRowsAreSelected) {
      return translate('warnings.noSelectedRows');
    }

    return '';
  }, [selectedRows, entries, translate]);

  const isMultipleSelected = selectedRows && selectedRows.length > 1;
  const rejectButtonTooltipText = useMemo(() => {
    if (noRowsAreSelected) {
      return translate('warnings.noSelectedRows');
    }

    if (isMultipleSelected) {
      return translate('messages.disabledRejectMultipleInvoices');
    }

    return '';
  }, [selectedRows, entries, translate, isMultipleSelected]);

  const exportButtonTooltipText = useMemo(() => {
    if (hasNoExportableInvoices) {
      return translate('messages.exportOnlyCreditNotesSelected');
    }
    if (shouldShowAlreadyPaidForExport) {
      return translate('messages.disabledPayInvoicesAlreadyPaid');
    }
    if (isIbanNotExportable) {
      return translate('messages.disabledExportNotExportable', {
        companyName: company?.name || ''
      });
    }
    if (isNoDocumentTypeForExport) {
      return translate('messages.disabledExportNoDocumentType');
    }
    if (isCounterpartyNotValidatedForExport || shouldDisablePayForExport) {
      return translate('messages.disabledPayInvoices');
    }
    if (isNotExportableForExport) {
      return translate('messages.disabledExportNotPayable');
    }

    return '';
  }, [
    selectedRows,
    hasNoExportableInvoices,
    shouldShowAlreadyPaidForExport,
    isNoDocumentTypeForExport,
    isCounterpartyNotValidatedForExport,
    shouldDisablePayForExport,
    isNotExportableForExport,
    isIbanNotExportable,
    entries,
    translate
  ]);

  const downloadButtonTooltipText = useMemo(() => {
    if (noRowsAreSelected) {
      return translate('warnings.noSelectedRows');
    }
    if (shouldDisableDownload) {
      return translate('messages.disabledDownloadInvoices');
    }
    return '';
  }, [selectedRows, entries, translate]);

  useEffect(() => {
    if (!companies?.length) {
      return;
    }
    setSelectedCompanyId(companies[0].id);
  }, [companies?.length]);

  useEffect(() => {
    setIsExportButtonDisabled(shouldDisableExport);
  }, [shouldDisableExport]);

  useEffect(() => {
    setExportTooltip(exportButtonTooltipText);
  }, [exportButtonTooltipText]);

  return (
    <Box
      sx={{
        ...gridToolbarContainerStyle
      }}
    >
      <Box sx={gridToolbarOutsideContainerStyle}>
        {handleDeleteClick && (
          <Tooltip title={deleteButtonTooltipText}>
            <span>
              <Button
                onClick={handleDeleteClick}
                color="error"
                sx={toolbarButtonStyle}
                disabled={
                  noRowsAreSelected || shouldDisableDelete || isDeleteDisabled
                }
                startIcon={<DeleteTwoTone sx={toolbarIconsStyle} />}
              >
                {translate('buttons.delete')}
              </Button>
            </span>
          </Tooltip>
        )}
        {handleApproveClick && (
          <Tooltip title={approveButtonTooltipText}>
            <span>
              <Button
                onClick={handleApproveClick}
                color="primary"
                sx={toolbarButtonStyle}
                disabled={noRowsAreSelected}
                startIcon={<Checklist sx={toolbarIconsStyle} />}
              >
                {translate('buttons.approve')}
              </Button>
            </span>
          </Tooltip>
        )}
        {handleRejectClick && (
          <Tooltip title={rejectButtonTooltipText}>
            <span>
              <Button
                onClick={handleRejectClick}
                color="primary"
                sx={toolbarButtonStyle}
                disabled={noRowsAreSelected || isMultipleSelected}
                startIcon={
                  <PlaylistRemove
                    sx={{
                      width: { md: '1.4rem', lg: '1.8rem' },
                      height: { md: '1.4rem', lg: '1.8rem' },
                      mr: { sm: '0.1rem', md: '0.1rem', lg: '0.2rem' }
                    }}
                  />
                }
              >
                {translate('buttons.reject')}
              </Button>
            </span>
          </Tooltip>
        )}
        {handlePayClick && (
          <Tooltip title={payButtonTooltipText}>
            <span>
              <Button
                onClick={handlePayClick}
                color="primary"
                sx={toolbarButtonStyle}
                disabled={noRowsAreSelected || shouldDisablePay}
                startIcon={<Payment sx={toolbarIconsStyle} />}
              >
                {translate('buttons.pay')}
              </Button>
            </span>
          </Tooltip>
        )}
        {handleAccountClick && (
          <Tooltip title={bookButtonTooltipText}>
            <span>
              <Button
                onClick={handleAccountClick}
                color="primary"
                sx={toolbarButtonStyle}
                disabled={noRowsAreSelected || shouldDisableBook}
                startIcon={<AccountBalanceWallet sx={toolbarIconsStyle} />}
              >
                {translate('buttons.book')}
              </Button>
            </span>
          </Tooltip>
        )}
        {handleSettleClick && (
          <Tooltip title={settleButtonTooltipText}>
            <span>
              <Button
                onClick={handleSettleClick}
                color="primary"
                sx={toolbarButtonStyle}
                disabled={noRowsAreSelected || shouldDisableSettle}
                startIcon={<Handshake sx={toolbarIconsStyle} />}
              >
                {translate('buttons.settle')}
              </Button>
            </span>
          </Tooltip>
        )}
        {handleDownloadClick && (
          <Tooltip title={downloadButtonTooltipText}>
            <span>
              <Button
                color="primary"
                sx={toolbarButtonStyle}
                onClick={handleDownloadClick}
                disabled={noRowsAreSelected || shouldDisableDownload}
                startIcon={<Download sx={toolbarIconsStyle} />}
              >
                {translate('buttons.download')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          ...gridToolbarContainerStyle,
          justifyContent: isDefaultToolbarHidden ? 'flex-end' : 'space-between'
        }}
      >
        {!isDefaultToolbarHidden && (
          <DefaultGridToolbar sx={defaultGridToolbarStyle} />
        )}
        {handleAddClick && (
          <Button color="primary" startIcon={<Add />} onClick={handleAddClick}>
            {translate('buttons.add')}
          </Button>
        )}
        {handleExportExcelClick && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleExportExcelClick}
            disabled={isExportDisabled}
          >
            {translate('buttons.exportExcel')}
          </Button>
        )}
        <GridToolbarContainer
          sx={{
            ...gridToolbarStyle,
            justifyContent,
            gap: { xs: 0, sm: 0, md: 0, lg: 1 }
          }}
        >
          {handleCreateClick && (
            <Button
              color="primary"
              startIcon={<Add sx={toolbarIconsStyle} />}
              sx={toolbarButtonStyle}
              onClick={() => {
                if (isOnAllCompanies) {
                  openSelectCompanyIdModal();
                  return;
                }
                handleCreateClick();
              }}
            >
              {translate('buttons.create')}
            </Button>
          )}
          {handleUploadInvoice && (
            <Button
              color="primary"
              startIcon={<FileUpload sx={toolbarIconsStyle} />}
              sx={toolbarButtonStyle}
              component="label"
            >
              {translate('buttons.upload')}
              <input
                hidden
                accept=".pdf, .png, .jpg, .jpeg, .heic"
                type="file"
                name="invoice"
                multiple
                onChange={(e) => {
                  if (!handleUploadInvoice) {
                    return;
                  }
                  handleUploadInvoice(e.target.files as FileList, 'button');
                  e.target.value = '';
                }}
              />
            </Button>
          )}
        </GridToolbarContainer>
      </Box>
      {companies?.length ? (
        <Modal
          headerTitle={translate('labels.createDocument')}
          isOpen={isSelectCompanyIdModalOpen}
          hide={closeSelectCompanyIdModal}
          maxWidth="sm"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <FormControl fullWidth>
              <InputLabel>{`${translate('labels.company')}*`}</InputLabel>
              <Select
                label={translate('labels.company')}
                value={selectedCompanyId || companies[0].id}
                onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
              >
                {companies.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              sx={{ alignSelf: 'flex-end' }}
              onClick={() => {
                closeSelectCompanyIdModal();
                if (handleCreateClick) {
                  handleCreateClick();
                }
              }}
            >
              {translate('buttons.create')}
            </Button>
          </Box>
        </Modal>
      ) : null}
    </Box>
  );
};
